import React, { FC, useState } from "react";

import { getLinkedFirm } from "../../sessionStorage/sessionStorage";
import {
  useCanManageCustomFieldsQuery,
  useGetCustomFieldsQuery,
} from "../../slices/CustomFieldsSlice";
import {
  CustomFieldModal,
  DragIcon,
  NoAccessModal,
  TrashIcon,
} from "./CustomFieldModals";
import {
  CustomField,
  CustomFieldEntity,
  CustomFieldValues,
} from "./customFields.types";
import { fieldsFor, valueError } from "./customFields.utils";
import { useFieldList } from "./useFieldList";

const INPUT_CLASSES =
  "w-full rounded-[10px] border bg-white px-[13px] py-[9px] text-[14px] text-[#1F2433] outline-none focus:border-[#6B4EFF] focus:shadow-[0_0_0_3px_rgba(107,78,255,0.12)]";

type Props = {
  entityType: CustomFieldEntity;
  values: CustomFieldValues;
  onChange: (fieldId: string, value: string) => void;
  showErrors?: boolean;
};

/**
 * "Custom Fields" in a client's Edit window: fill in the firm's fields.
 * Creating, reordering and deleting fields needs Settings access; others get
 * the "You don't have access" message.
 */
export const CustomFieldsEditSection: FC<Props> = ({
  entityType,
  values,
  onChange,
  showErrors,
}) => {
  const orgId = getLinkedFirm()?.orgId || "";
  const { data: allFields } = useGetCustomFieldsQuery(
    { orgId },
    { skip: !orgId },
  );
  const { data: canManage } = useCanManageCustomFieldsQuery(
    { orgId },
    { skip: !orgId },
  );
  const fields = fieldsFor(allFields, entityType);
  const { ordered, dragProps, askDelete, deleteModal } = useFieldList(
    entityType,
    fields,
  );
  const [creating, setCreating] = useState(false);
  const [noAccess, setNoAccess] = useState(false);

  return (
    <div>
      <div className={"mb-1.5 flex items-center gap-1.5"}>
        <span
          className={
            "text-[11px] font-bold uppercase tracking-[0.8px] text-[#3F4757]"
          }>
          Custom Fields
        </span>
        <span
          title={
            "Fill in your custom field values. To add or manage the field list, go to Settings › Custom Fields."
          }
          className={
            "inline-flex h-[15px] w-[15px] flex-none cursor-help items-center justify-center rounded-full bg-[#E7E9F0] text-[#8A92A3]"
          }>
          <svg
            width={"9"}
            height={"9"}
            viewBox={"0 0 24 24"}
            fill={"none"}
            stroke={"currentColor"}
            strokeWidth={"3"}
            strokeLinecap={"round"}
            strokeLinejoin={"round"}>
            <line x1={"12"} y1={"10"} x2={"12"} y2={"16"} />
            <circle cx={"12"} cy={"6.2"} r={"0.4"} />
          </svg>
        </span>
      </div>

      {ordered.map((field: CustomField) => {
        const drag = dragProps(field);
        const value = values[field.id] ?? "";
        const error = showErrors ? valueError(field, value) : null;
        const inputId = `cf-value-${field.id}`;
        const keepOld =
          field.fieldType === "dropdown" &&
          value &&
          !field.choices.includes(value);
        return (
          <div
            key={field.id}
            onDragEnter={canManage ? drag.row.onDragEnter : undefined}
            onDragOver={canManage ? drag.row.onDragOver : undefined}
            onDrop={canManage ? drag.row.onDrop : undefined}
            className={`mb-3 flex items-end gap-1.5 rounded-[9px] transition-[opacity,box-shadow] ${
              drag.row.dragging
                ? "bg-[#F6F4FF] opacity-40 shadow-[0_8px_20px_rgba(20,22,34,0.16)]"
                : ""
            }`}>
            {canManage && (
              <span
                {...drag.handle}
                className={
                  "flex h-10 w-5 flex-none cursor-grab items-center justify-center text-[#B4BAC7] hover:text-[#6B4EFF]"
                }>
                <DragIcon />
              </span>
            )}
            <div className={"min-w-0 flex-1"}>
              <label
                htmlFor={inputId}
                className={
                  "mb-1.5 block break-words text-[12.5px] font-semibold text-[#5B6472]"
                }>
                {field.label}
              </label>
              {field.fieldType === "dropdown" ? (
                <select
                  id={inputId}
                  value={value}
                  onChange={(event) => onChange(field.id, event.target.value)}
                  className={`${INPUT_CLASSES} border-[#E2E6EE]`}>
                  <option value={""}>Select</option>
                  {keepOld && <option value={value}>{value}</option>}
                  {field.choices.map((choice) => (
                    <option key={choice} value={choice}>
                      {choice}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  id={inputId}
                  value={value}
                  maxLength={500}
                  inputMode={field.fieldType === "number" ? "decimal" : "text"}
                  placeholder={
                    field.fieldType === "number"
                      ? "Enter a number"
                      : "Enter text or number"
                  }
                  onChange={(event) => onChange(field.id, event.target.value)}
                  className={`${INPUT_CLASSES} ${
                    error ? "border-[#E5484D]" : "border-[#E2E6EE]"
                  }`}
                />
              )}
              {error && (
                <div
                  className={"mt-1 text-[12px] font-semibold text-[#E5484D]"}>
                  {error}
                </div>
              )}
            </div>
            {canManage && (
              <button
                type={"button"}
                title={"Delete field"}
                aria-label={`Delete field ${field.label}`}
                onClick={() => askDelete(field)}
                className={
                  "flex h-10 w-8 flex-none items-center justify-center rounded-lg text-[#B4BAC7] hover:bg-[#FEF2F2] hover:text-[#E5484D]"
                }>
                <TrashIcon />
              </button>
            )}
          </div>
        );
      })}

      <button
        type={"button"}
        onClick={() => (canManage ? setCreating(true) : setNoAccess(true))}
        className={
          "mt-1 inline-flex items-center gap-1.5 rounded-[9px] border border-dashed border-[#B9AEF5] bg-[#F7F5FF] px-[15px] py-[9px] text-[13px] font-semibold text-[#6B4EFF] hover:bg-[#F1EEFF]"
        }>
        <svg
          width={"15"}
          height={"15"}
          viewBox={"0 0 24 24"}
          fill={"none"}
          stroke={"currentColor"}
          strokeWidth={"2.6"}
          strokeLinecap={"round"}>
          <path d={"M12 5v14M5 12h14"} />
        </svg>
        Create new custom field
      </button>

      {creating && (
        <CustomFieldModal
          entityType={entityType}
          onClose={() => setCreating(false)}
        />
      )}
      {noAccess && <NoAccessModal onClose={() => setNoAccess(false)} />}
      {deleteModal}
    </div>
  );
};
